import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

/**
 * InstallPrompt — shows a subtle bottom sheet inviting the user to install
 * the PWA. Captures the beforeinstallprompt event (Chrome/Edge/Android).
 * On iOS Safari (no native prompt), shows manual "Add to Home Screen" instructions.
 */
const DISMISS_KEY = 'mbe_install_dismissed_at';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    // Check if already installed / running in standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    // Check dismiss cooldown
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || '0');
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

    // iOS detection (Safari doesn't fire beforeinstallprompt)
    const ua = window.navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iOS);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay so we don't interrupt onboarding
      setTimeout(() => setVisible(true), 8000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show manual hint after delay
    if (iOS) {
      setTimeout(() => setVisible(true), 12000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setVisible(false);
      } else {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setVisible(false);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSHelp(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIOSHelp(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      style={{
        position: 'fixed',
        left: '12px', right: '12px', bottom: '84px',
        zIndex: 999,
        animation: 'slideUp 0.35s ease',
      }}
    >
      {!showIOSHelp ? (
        <div style={{
          background: 'linear-gradient(135deg, #FFFFFF 0%, #FFFBF2 100%)',
          borderRadius: '18px',
          padding: '14px 16px',
          boxShadow: '0 12px 40px rgba(15, 23, 42, 0.18)',
          border: '1px solid rgba(245, 166, 35, 0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '480px',
          margin: '0 auto',
        }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '12px',
            background: 'linear-gradient(135deg,#F5A623,#E8951A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Smartphone size={20} color="#FFF" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A', fontFamily: 'Poppins, sans-serif' }}>
              Install MBE Signal
            </div>
            <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>
              Faster access & push alerts. Takes 2 seconds.
            </div>
          </div>
          <button
            data-testid="pwa-install-btn"
            onClick={handleInstall}
            style={{
              padding: '8px 14px', borderRadius: '10px', border: 'none',
              background: 'linear-gradient(135deg,#F5A623,#E8951A)',
              color: '#FFF', fontSize: '12px', fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <Download size={12} /> Install
          </button>
          <button
            data-testid="pwa-dismiss-btn"
            onClick={handleDismiss}
            aria-label="Dismiss"
            style={{
              background: 'transparent', border: 'none', padding: '4px',
              color: '#94A3B8', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div style={{
          background: '#FFFFFF',
          borderRadius: '18px',
          padding: '16px',
          boxShadow: '0 12px 40px rgba(15, 23, 42, 0.18)',
          border: '1px solid #E2E8F0',
          maxWidth: '480px',
          margin: '0 auto',
          position: 'relative',
        }}>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            style={{
              position: 'absolute', top: '8px', right: '8px',
              background: 'transparent', border: 'none', padding: '4px',
              color: '#94A3B8', cursor: 'pointer',
            }}
          ><X size={16} /></button>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A', marginBottom: '8px' }}>
            Add MBE Signal to Home Screen
          </div>
          <ol style={{ fontSize: '12px', color: '#475569', lineHeight: 1.7, paddingLeft: '20px', margin: 0 }}>
            <li>Tap the <strong>Share</strong> button (⬆) in Safari</li>
            <li>Scroll and select <strong>Add to Home Screen</strong></li>
            <li>Tap <strong>Add</strong> to install</li>
          </ol>
        </div>
      )}
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default InstallPrompt;
